import { useEffect, useRef, useState } from "react";
import WaterFirstPanel from "./first";
import WaterSecondPanel from "./second";
import WaterThirdPanel from "./third";
import { findSensorParameterDtoBySensorIds, getSensorByPrimitiveUuid, getSensorDetail } from "@/api/device/sensor";
import { Sensor, SensorParameter } from "@/global/interface/device/sensor";
import { queryInfluxCurrentData, queryInfluxDataList } from "@/api/influx/influx";
import { TimeValue } from "@/global/interface/time-value";
import TnMessage from "@/global/components/message";
import { Skeleton } from "antd";
import { Transfer2DisplayScale } from "@/global/utils/unit-type";
import { emitter } from "@/global/listeners";
import { Emit_Event } from "@/enums/emit-event";

interface IProps {
    primitiveUuid: string
}

export default function FluviographRightMainPanel(props: IProps) {

    const [waterLevel, setWaterLevel] = useState<number>(0); // 水位值
    const [sensor, setSensor] = useState<Sensor>(); // 水位计
    const intervalRef = useRef<NodeJS.Timeout>(null); // 定时器
    const [waterData, setWaterData] = useState<number[][]>([]) // 水位曲线
    const [curLoading, setCurLoading] = useState<boolean>(false) // 当前值loading
    const [hisLoading, setHisLoading] = useState<boolean>(false) // 曲线loading
    const [wlevelsParams, setWlevelParams] = useState<SensorParameter>(null) // 水位参数
    const [endTime, setEndTime] = useState<number>(null)

    useEffect(() => {
        if (!props.primitiveUuid) {
            return;
        }
        generateData();
        return () => {
            clearIntervalRef();
        };
    }, [props.primitiveUuid, endTime]);

    useEffect(() => {
        if (wlevelsParams) {
            clearIntervalRef();
            startInterval();
        }
    }, [wlevelsParams])

    useEffect(() => {
        emitter.on(Emit_Event.LINE_CHART_UPDATE, generateData)

        return (() => {
            emitter.off(Emit_Event.LINE_CHART_UPDATE, generateData)
        })
    }, [])


    const generateData = () => {
        setSensor(null);
        setWaterLevel(0);
        setWaterData([]);
        getSensorByPrimitiveUuid(props.primitiveUuid).then(res => {
            let sensor: Sensor = res.data;
            setSensor(sensor);

            findSensorParameterDtoBySensorIds([sensor.id]).then(res => {
                let sensorParams: SensorParameter[] = res.data || []
                // 获取symbol类型为wlevel的参数
                let wlevelParams: SensorParameter = sensorParams.find(item => item.symbol == "wlevel")
                if (wlevelParams) {
                    setWlevelParams(wlevelParams);
                    getCurrentData(wlevelParams);
                    getHistoryData(wlevelParams);
                } else {
                    TnMessage.warning("未找到水位数据")
                }
            }).catch(e => {
                TnMessage.error("获取数据失败！")
            })
        });
    }


    // 开启定时器
    const startInterval = () => {
        if (intervalRef.current) return;
        intervalRef.current = setInterval(() => {
            getCurrentData(wlevelsParams);
            getHistoryData(wlevelsParams);
        }, 5000);
    };

    // 获取当前值
    const getCurrentData = (wlevelsParams: SensorParameter) => {
        if (!wlevelsParams) return;
        queryInfluxCurrentData(wlevelsParams.id, "wlevel", "sensor").then(res => {
            let value: TimeValue = res.data
            if (value) {
                setWaterLevel(value.value * Transfer2DisplayScale(wlevelsParams.unit))
            }
        })
    }

    // 获取历史曲线值
    const getHistoryData = (wlevelsParams: SensorParameter) => {
        if (!wlevelsParams) return;
        queryInfluxDataList(wlevelsParams?.id, "wlevel", "sensor", {
            limit: 100,
            endTime: endTime
        }).then(res => {
            let valueList: TimeValue[] = res.data || []
            // 组合成数据
            let data = []
            for (let i = 0; i < valueList?.length; i++) {
                data.push([valueList[i].time, valueList[i].value * Transfer2DisplayScale(wlevelsParams.unit)])
            }
            setWaterData(data)
        }).catch(() => {
            TnMessage.error("获取历史数据异常")
        }).finally(() => {
            setHisLoading(false);
        })

    }

    // 清除定时器
    const clearIntervalRef = () => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
    };


    return (
        <>
            <div className="device-code-area"><span className="device-code">{sensor?.code || ''}</span></div>
            <div className="panel-item">
                <Skeleton loading={curLoading}>
                    <WaterFirstPanel waterLevel={waterLevel} />
                </Skeleton>
            </div>
            <div className="panel-item">
                <Skeleton loading={hisLoading}>
                    <WaterSecondPanel setEndTime={setEndTime} waterData={waterData} param={wlevelsParams} />
                </Skeleton>
            </div>
            <div className="panel-item">
                <WaterThirdPanel sensor={sensor} />
            </div>
        </>
    );
}
